<template>
  <div>
    <validation-observer ref="shiftObserver">
      <b-sidebar
        id="addEditShift"
        bg-variant="white"
        right
        backdrop
        shadow
        width="520px"
        class="sidebar-form"
        @hidden="sidebarToggle"
      >
        <h3 class="form-title">
          {{ id ? 'Edit' : 'Add' }} Shift
        </h3>
        <div class="line user-underline" />

        <!-- ── Section 1: Basic Information ── -->
        <p class="section-title mt-1 mb-75">
          Basic Information
        </p>

        <!-- Shift Name -->
        <b-form-group
          label="Shift Name"
          label-for="shift-name"
          class="required-form-filed"
        >
          <validation-provider
            #default="{ errors }"
            name="Shift Name"
            rules="required|max:100"
          >
            <b-form-input
              id="shift-name"
              v-model="name"
              :state="errors.length > 0 ? false : null"
              placeholder="e.g. General Shift"
            />
            <small class="text-danger">{{ errors[0] }}</small>
          </validation-provider>
        </b-form-group>

        <!-- Shift Code -->
        <b-form-group
          label="Shift Code"
          label-for="shift-code"
          class="required-form-filed"
        >
          <validation-provider
            #default="{ errors }"
            name="Shift Code"
            rules="required|max:20"
          >
            <b-form-input
              id="shift-code"
              v-model="code"
              :state="errors.length > 0 ? false : null"
              placeholder="e.g. GEN-01"
            />
            <small class="text-danger">{{ errors[0] }}</small>
          </validation-provider>
        </b-form-group>

        <!-- ── Section 2: Shift Timing ── -->
        <p class="section-title mt-1 mb-75">
          Shift Timing
        </p>

        <!-- Start Time / End Time side by side -->
        <b-row>
          <b-col cols="6">
            <b-form-group
              label="Start Time"
              label-for="start-time"
              class="required-form-filed"
            >
              <validation-provider
                #default="{ errors }"
                name="Start Time"
                rules="required"
              >
                <b-form-input
                  id="start-time"
                  v-model="start_time"
                  type="time"
                  :state="errors.length > 0 ? false : null"
                  @change="calculateWorkingHours"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>
          <b-col cols="6">
            <b-form-group
              label="End Time"
              label-for="end-time"
              class="required-form-filed"
            >
              <validation-provider
                #default="{ errors }"
                name="End Time"
                rules="required"
              >
                <b-form-input
                  id="end-time"
                  v-model="end_time"
                  type="time"
                  :state="(errors.length > 0 || endTimeError) ? false : null"
                  @change="calculateWorkingHours"
                />
                <small
                  v-if="endTimeError"
                  class="text-danger"
                >Must be after Start Time</small>
                <small
                  v-else
                  class="text-danger"
                >{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>
        </b-row>

        <!-- Working Hours (read-only, auto-calculated) -->
        <b-form-group
          label="Working Hours"
          label-for="working-hours"
        >
          <b-form-input
            id="working-hours"
            :value="working_hours"
            readonly
            placeholder="Auto-calculated from start/end time"
            class="bg-light"
          />
        </b-form-group>

        <!-- Minimum Full Day Hours & Half Day Hours side by side -->
        <b-row>
          <b-col cols="6">
            <b-form-group
              label="Min. Full Day Hours"
              label-for="min-full-day"
              class="required-form-filed"
            >
              <validation-provider
                #default="{ errors }"
                name="Minimum Full Day Hours"
                rules="required"
              >
                <b-form-input
                  id="min-full-day"
                  v-model="minimum_full_day_hours"
                  type="text"
                  :state="(errors.length > 0 || minFullDayError || minFullDayOverError) ? false : null"
                  placeholder="e.g. 08:00"
                  @input="minFullDayError = false; minFullDayOverError = false"
                  @keydown="handleTimeKeydown($event, 'minimum_full_day_hours')"
                />
                <small v-if="minFullDayOverError" class="text-danger">Cannot exceed total working hours</small>
                <small v-else-if="minFullDayError" class="text-danger">Must be greater than 00:00</small>
                <small v-else class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>

          <b-col cols="6">
            <b-form-group
              label="Half Day Hours"
              label-for="half-day"
              class="required-form-filed"
            >
              <validation-provider
                #default="{ errors }"
                name="Half Day Hours"
                rules="required"
              >
                <b-form-input
                  id="half-day"
                  v-model="half_day_hours"
                  type="text"
                  :state="(errors.length > 0 || halfDayError || halfDayOverError) ? false : null"
                  placeholder="e.g. 04:00"
                  @input="halfDayError = false; halfDayOverError = false"
                  @keydown="handleTimeKeydown($event, 'half_day_hours')"
                />
                <small v-if="halfDayOverError" class="text-danger">Cannot exceed total working hours</small>
                <small v-else-if="halfDayError" class="text-danger">Must be greater than 00:00</small>
                <small v-else class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>
        </b-row>

        <!-- ── Section 3: Weekly Configuration ── -->
        <p class="section-title mt-1 mb-75">
          Weekly Configuration
        </p>

        <!-- Working Days -->
        <b-form-group
          label="Working Days"
          label-for="working-days"
          class="required-form-filed"
        >
          <weekday-selector
            id="working-days"
            v-model="working_days"
            :blocked-days="weekly_off_days"
            sm
            active-color="#2178fb"
            shadow-color="rgba(33,120,251,0.35)"
          />
          <small v-if="workingDaysError" class="text-danger d-block mt-25">Working Days is required</small>
          <small
            v-if="workingDayOverlapError"
            class="text-danger d-block mt-25"
          >Working Days and Weekly Off Days cannot overlap</small>
        </b-form-group>

        <!-- Weekly Off Days -->
        <b-form-group
          label="Weekly Off Days"
          label-for="weekly-off-days"
        >
          <weekday-selector
            id="weekly-off-days"
            v-model="weekly_off_days"
            :blocked-days="working_days"
            sm
            active-color="#ff9f43"
            shadow-color="rgba(255,159,67,0.35)"
          />
          <small
            v-if="weeklyOffOverlapError"
            class="text-danger d-block mt-25"
          >Weekly Off Days cannot overlap with Working Days</small>
        </b-form-group>

        <b-form-group
          label="Minimum hours required per day"
          label-for="grace-period-start"
        >
          <validation-provider
            #default="{ errors }"
            name="Minimum hours required per day"
            rules="required"
          >
            <b-form-input
              id="grace-period-start"
              v-model="grace_period_start_hours"
              type="text"
              :state="(errors.length > 0 || gracePeriodStartError || gracePeriodStartOverError) ? false : null"
              placeholder="e.g. 06:00"
              @input="gracePeriodStartError = false; gracePeriodStartOverError = false"
              @keydown="handleTimeKeydown($event, 'grace_period_start_hours')"
            />
            <small v-if="gracePeriodStartOverError" class="text-danger">Cannot exceed total working hours</small>
            <small v-else-if="gracePeriodStartError" class="text-danger">Must be greater than 00:00</small>
            <small v-else class="text-danger">{{ errors[0] }}</small>
          </validation-provider>
        </b-form-group>

         <b-form-group
          label="Maximum days allowed in a week"
          label-for="grace-days-allowed"
          class="required-form-filed mt-1"
        >
          <validation-provider
            #default="{ errors }"
            name="Maximum days allowed in a week"
            rules="required|min_value:0|max_value:7"
          >
            <b-form-input
              id="grace-days-allowed"
              v-model="grace_days_allowed"
              type="number"
              :state="errors.length > 0 ? false : null"
              placeholder="e.g. 2"
              min="0"
              max="7"
            />
            <small class="text-danger">{{ errors[0] }}</small>
          </validation-provider>
        </b-form-group>
        <!-- ── Section 4: Grace Period ── -->
        <p class="section-title mt-1 mb-75">
          Grace Period
        </p>

        <b-form-group
          label="Grace Minutes"
          label-for="grace-minutes"
          class="required-form-filed"
        >
          <validation-provider
            #default="{ errors }"
            name="Grace Minutes"
            rules="required|numeric|min_value:0"
          >
            <b-form-input
              id="grace-minutes"
              v-model="grace_minutes"
              type="number"
              :state="errors.length > 0 ? false : null"
              placeholder="0"
              min="0"
            />
            <small class="text-danger">{{ errors[0] }}</small>
          </validation-provider>
        </b-form-group>

        <!-- ── Section 5: Lunch Break ── -->
        <p class="section-title mt-1 mb-75">
          Lunch Break
        </p>

        <b-form-group
          label="Lunch Break (Minutes)"
          label-for="lunch-break-minutes"
          class="required-form-filed"
        >
          <validation-provider
            #default="{ errors }"
            name="Lunch Break"
            rules="required|numeric|min_value:0"
          >
            <b-form-input
              id="lunch-break-minutes"
              v-model="lunch_break_minutes"
              type="number"
              :state="errors.length > 0 ? false : null"
              placeholder="0"
              min="0"
              @change="calculateWorkingHours"
            />
            <small class="text-danger">{{ errors[0] }}</small>
          </validation-provider>
        </b-form-group>

        <!-- ── Section 6: Status ── -->
        <p class="section-title mt-1 mb-75">
          Status
        </p>

        <b-form-group
          label="Active"
          label-for="shift-status"
          class="d-flex justify-content-between align-items-center"
        >
          <b-form-checkbox
            v-model="status"
            class="custom-control-success"
            name="shift-status"
            size="md"
            switch
          >
            <span class="switch-icon-left">
              <feather-icon icon="CheckIcon" />
            </span>
            <span class="switch-icon-right">
              <feather-icon icon="XIcon" />
            </span>
          </b-form-checkbox>
        </b-form-group>

        <!-- Footer -->
        <template #footer>
          <div class="form-button d-flex my-2">
            <button
              class="btn side-bar-btn"
              :disabled="btnLoader"
              @click.prevent="validationForm"
            >
              <b-spinner
                v-if="btnLoader"
                small
                class="spinner"
              />
              {{ id ? 'Update' : 'Save' }}
            </button>
            <button
              class="btn side-bar-btn"
              @click="$root.$emit('bv::toggle::collapse', 'addEditShift')"
            >
              Cancel
            </button>
          </div>
        </template>
      </b-sidebar>
    </validation-observer>
  </div>
</template>

<script>
import AddEditShiftMixin from '@/mixin/masterSetting/Shift/AddEditShift'

export default {
  mixins: [AddEditShiftMixin],
}
</script>

<style scoped lang="scss">
@import "../../../assets/scss/component-css/addEditShift.scss";
</style>
